// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { ProductInformationDocumentsModel } from "@stoneage/App/Services/Documents/DocumentsService";
import { Draft } from "immer";

type ProductInformationState = Readonly<{
    isLoading: boolean;
    data: ProductInformationDocumentsModel | undefined;
    openDrawer: boolean;
    filters: {
        onlyShowMyProducts: boolean;
        doFacetedSearches: boolean;
        page: number;
        pageSize?: number;
        sortBy?: string;
        applications: string[];
        documentTypes: string[];
        productLines: string[];
        onlyShowActiveDocuments?: boolean;
        onlyShowDiscontinuedDocuments?: boolean;
        query?: string;
    };
}>;

const initialProductInfoState: ProductInformationState = {
    isLoading: true,
    data: undefined,
    openDrawer: false,
    filters: {
        onlyShowMyProducts: false,
        doFacetedSearches: true,
        page: 1,
        applications: [],
        documentTypes: [],
        productLines: [],
        onlyShowActiveDocuments: true,
        onlyShowDiscontinuedDocuments: false,
        query: "",
    },
};

const reducer = {
    "Pages/ProductInformation/BeginLoadingDocuments": (draft: Draft<ProductInformationState>) => {
        draft.isLoading = true;
    },
    "Pages/ProductInformation/AssignDocumentsToData": (
        draft: Draft<ProductInformationState>,
        payload: { data: ProductInformationDocumentsModel },
    ) => {
        draft.filters.page = payload.data?.pagination.page;
        draft.filters.pageSize = payload.data?.pagination.pageSize;
        draft.filters.sortBy = payload.data?.pagination.sortType;
        draft.data = payload.data;
        draft.isLoading = false;
    },
    "Pages/ProductInformation/CompletedLoadingDocuments": (draft: Draft<ProductInformationState>) => {
        draft.isLoading = false;
    },
    "Pages/ProductInformation/AddFilter": (
        draft: Draft<ProductInformationState>,
        payload: { name: string; value: string | boolean | number },
    ) => {
        switch (payload.name) {
            case "OnlyShowMyProducts":
                draft.filters.onlyShowMyProducts = payload.value as boolean;
                break;
            case "OnlyShowActiveDocuments":
                draft.filters.onlyShowActiveDocuments = payload.value as boolean;
                break;
            case "OnlyShowDiscontinuedDocuments":
                draft.filters.onlyShowDiscontinuedDocuments = payload.value as boolean;
                break;
            case "PageNumber":
                draft.filters.page = payload.value as number;
                break;
            case "PageSize":
                draft.filters.pageSize = payload.value as number;
                draft.filters.page = 1;
                break;
            case "SortType":
                draft.filters.sortBy = payload.value as string;
                break;
            case "Application":
                if (draft.filters.applications.indexOf(payload.value as string) === -1) {
                    draft.filters.applications.push(payload.value as string);
                    draft.filters.page = 1;
                }
                break;
            case "DocumentTypes":
                if (draft.filters.documentTypes.indexOf(payload.value as string) === -1) {
                    draft.filters.documentTypes.push(payload.value as string);
                    draft.filters.page = 1;
                }
                break;
            case "ProductLines":
                if (draft.filters.productLines.indexOf(payload.value as string) === -1) {
                    draft.filters.productLines.push(payload.value as string);
                    draft.filters.page = 1;
                }
                break;
            case "Query":
                draft.filters.query = payload.value as string;
                draft.filters.page = 1;
                break;
            default:
                break;
        }
    },
    "Pages/ProductInformation/RemoveFilter": (
        draft: Draft<ProductInformationState>,
        payload: { name: string; value: string[] | boolean },
    ) => {
        switch (payload.name) {
            case "OnlyShowMyProducts":
                draft.filters.onlyShowMyProducts = payload.value as boolean;
                draft.filters.page = 1;
                break;
            case "OnlyShowDiscontinuedDocuments":
                draft.filters.onlyShowDiscontinuedDocuments = payload.value as boolean;
                draft.filters.page = 1;
                break;
            case "OnlyShowActiveDocuments":
                draft.filters.onlyShowActiveDocuments = payload.value as boolean;
                draft.filters.page = 1;
                break;
            case "Application":
                draft.filters.applications = payload.value as string[];
                draft.filters.page = 1;
                break;
            case "DocumentTypes":
                draft.filters.documentTypes = payload.value as string[];
                draft.filters.page = 1;
                break;
            case "ProductLines":
                draft.filters.productLines = payload.value as string[];
                draft.filters.page = 1;
                break;
            case "Query":
                draft.filters.query = "";
                draft.filters.page = 1;
                break;
            default:
                break;
        }
    },
    "Pages/ProductInformation/ClearAllFilters": (draft: Draft<ProductInformationState>) => {
        draft.filters.onlyShowDiscontinuedDocuments = false;
        draft.filters.onlyShowMyProducts = false;
        draft.filters.onlyShowActiveDocuments = false;
        draft.filters.applications = [];
        draft.filters.productLines = [];
        draft.filters.documentTypes = [];
        draft.filters.query = "";
    },
    "Pages/ProductInformation/ToggleFiltersDrawer": (
        draft: Draft<ProductInformationState>,
        payload: { isOpen: boolean },
    ) => {
        draft.openDrawer = payload.isOpen;
    },
};

const xProductInfo = createTypedReducerWithImmer(initialProductInfoState, reducer);

(reducers as any).xProductInfo = xProductInfo;

export type xProductInfoState = {
    xProductInfo: ProductInformationState;
};

type xProductInfoReducers = {
    isLoading: typeof xProductInfo;
    data: typeof xProductInfo;
};

export type xProductInfoActions = Parameters<xProductInfoReducers[keyof xProductInfoReducers]>[1] | AnyAction;
